import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import type { MutableRefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

export type MetadataEditFormActions = {
  beginEdit: () => void
  cancelEdit: () => void
}

export type UseMetadataEditScopeFlowArgs = {
  /** Changes reset choose-scope / edit-scope UI (e.g. entry + user-edit ids). */
  resetKey: string
  hasRevisionFamily: boolean
  arePriorEditsReady: boolean
  onEditingChange?: (isBusy: boolean) => void
}

export type UseMetadataEditScopeFlowResult = {
  isChoosingScope: boolean
  editScope: MetadataEditScope | undefined
  notifyBusy: (isBusy: boolean) => void
  /**
   * Assign `{ beginEdit, cancelEdit }` after the form hook runs.
   * Ref update during render is intentional (same pattern as onEditingChangeRef).
   */
  formActionsRef: MutableRefObject<MetadataEditFormActions>
  handleEditClick: () => void
  handleCancelScope: () => void
  handleChooseScope: (scope: MetadataEditScope) => void
  handleChangeScope: () => void
  handleCancelEdit: () => void
}

export function useMetadataEditScopeFlow({
  resetKey,
  hasRevisionFamily,
  arePriorEditsReady,
  onEditingChange,
}: UseMetadataEditScopeFlowArgs): UseMetadataEditScopeFlowResult {
  const [isChoosingScope, setIsChoosingScope] = useState(false)
  const [editScope, setEditScope] = useState<MetadataEditScope | undefined>(
    undefined
  )
  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange

  const formActionsRef = useRef<MetadataEditFormActions>({
    beginEdit: () => undefined,
    cancelEdit: () => undefined,
  })

  const notifyBusy = useCallback((isBusy: boolean) => {
    onEditingChangeRef.current?.(isBusy)
  }, [])

  useEffect(() => {
    setIsChoosingScope(false)
    setEditScope(undefined)
  }, [resetKey])

  const handleEditClick = useCallback(() => {
    if (hasRevisionFamily) {
      setIsChoosingScope(true)
      notifyBusy(true)
      return
    }
    formActionsRef.current.beginEdit()
  }, [hasRevisionFamily, notifyBusy])

  const handleCancelScope = useCallback(() => {
    setIsChoosingScope(false)
    setEditScope(undefined)
    notifyBusy(false)
  }, [notifyBusy])

  const handleChooseScope = useCallback(
    (scope: MetadataEditScope) => {
      if (scope === 'family' && !arePriorEditsReady) return
      setEditScope(scope)
      setIsChoosingScope(false)
      formActionsRef.current.beginEdit()
    },
    [arePriorEditsReady]
  )

  const handleChangeScope = useCallback(() => {
    formActionsRef.current.cancelEdit()
    setIsChoosingScope(true)
    notifyBusy(true)
  }, [notifyBusy])

  const handleCancelEdit = useCallback(() => {
    formActionsRef.current.cancelEdit()
    setEditScope(undefined)
  }, [])

  return {
    isChoosingScope,
    editScope,
    notifyBusy,
    formActionsRef,
    handleEditClick,
    handleCancelScope,
    handleChooseScope,
    handleChangeScope,
    handleCancelEdit,
  }
}

export async function saveMetadataEditWithScope<TValues, TMember>(args: {
  editScope: MetadataEditScope | undefined
  hasRevisionFamily: boolean
  values: TValues
  familyMembers: readonly TMember[]
  onSave: (values: TValues) => Promise<void>
  onSaveFamily: (values: TValues, members: readonly TMember[]) => Promise<void>
  mutatePriorEdits: () => Promise<unknown>
}): Promise<void> {
  const {
    editScope,
    hasRevisionFamily,
    values,
    familyMembers,
    onSave,
    onSaveFamily,
    mutatePriorEdits,
  } = args
  if (editScope === 'family' && hasRevisionFamily) {
    await onSaveFamily(values, familyMembers)
    await mutatePriorEdits()
    return
  }
  await onSave(values)
  await mutatePriorEdits()
}
